import React, { useEffect } from 'react';
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";

export default function CapoBrainPrivacyPolicy() {
  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  const primaryColor = "#684df4";

  const onHover = (e) => {
    e.currentTarget.style.background = primaryColor;
    e.currentTarget.style.color = '#ffffff';
    e.currentTarget.style.transform = 'translateY(-8px) scale(1.025)';
    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.18)';
    e.currentTarget.style.borderColor = primaryColor;
    const icon = e.currentTarget.querySelector('i');
    if (icon) icon.style.color = '#ffffff';
  };

  const onLeave = (e) => {
    e.currentTarget.style.background = 'white';
    e.currentTarget.style.color = 'inherit';
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
    e.currentTarget.style.borderColor = '#a0a0a0';
    const icon = e.currentTarget.querySelector('i');
    if (icon) icon.style.color = primaryColor;
  };

  const cardStyle = {
    transition: 'all 0.28s ease',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #a0a0a0',
    backgroundColor: '#ffffff',
  };

  return (
    <div>
      <Helmet>
        <title>Privacy Policy - CapoBrain | Technic Mentors</title>
        <link rel="canonical" href="https://technicmentors.com/capobrain-privacy-policy" />
      </Helmet>

      {/* Hero Header */}
      <div className="allHeaderBg">
        <div className="allHeader-overlay d-flex align-items-center justify-content-center">
          <div>
            <h1 className="head text-center text-white animate__animated animate__zoomIn allHeaderP">
              CapoBrain - Privacy Policy
            </h1>
          </div>
        </div>
      </div>
 
      {/* Introduction */}
      <div className="container mt-5" data-aos="fade-up">
        <div className="section-title text-center">
          <h2>Your Privacy Matters</h2>
          <h3><span>CapoBrain Privacy Policy</span></h3>
        </div>
         <strong>Effective Date: 3rd April 2026.</strong>
        <p style={{ textAlign: "justify" }}>
          Welcome to <strong>CapoBrain</strong>, a school and educational institute management system developed
          and operated by{" "}
          <a href="https://technicmentors.com" target="_blank" rel="noopener" style={{ textDecoration: "none", color: primaryColor }}>
            Technic Mentors
          </a>. This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you use the CapoBrain mobile application and related services.
        </p>
        <p style={{ textAlign: "justify" }}>
          CapoBrain is designed to facilitate communication, academic management, and administrative operations
          within educational institutions. By using the App, you agree to the terms of this Privacy Policy.{" "}
        
        </p>
      </div>

      {/* Quick Overview Cards */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="row g-4">
          {[
            { icon: "fa-shield-halved", title: "Data Protection" },
            { icon: "fa-user-lock", title: "Privacy Rights" },
            { icon: "fa-mobile-screen", title: "App Permissions" },
            { icon: "fa-lock", title: "Secure & Encrypted" },
            { icon: "fa-eye-slash", title: "No Data Selling" },
            { icon: "fa-scale-balanced", title: "Legal Compliance" },
          ].map((item, i) => (
            <div className="col-md-4" key={i}>
              <div className="card shadow-sm h-100" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <div className="card-body d-flex align-items-center p-4">
                  <i className={`fa-solid ${item.icon} fa-lg me-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                  <p className="card-title mb-0 fw-medium">{item.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scope of Policy */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Scope of <span>This Policy</span></h3>
          <p className="lead" style={{ color: "#555" }}>This Privacy Policy applies to all users of the CapoBrain system:</p>
        </div>
        <div className="row g-4 justify-content-center">
          {[
            { icon: "fa-user-graduate", text: "Students" },
            { icon: "fa-users", text: "Parents / Guardians" },
            { icon: "fa-chalkboard-user", text: "Teachers" },
            { icon: "fa-user-tie", text: "School Administrators" },
            { icon: "fa-id-badge", text: "Staff Members" },
          ].map((item, i) => (
            <div className="col-md-4 col-lg-2" key={i}>
              <div className="card shadow-sm h-100 text-center p-4" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                <p className="mb-0 fw-medium" style={{ fontSize: '0.9rem' }}>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Information We Collect */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Information <span>We Collect</span></h3>
        </div>
        <div className="row g-4">
          {[
            {
              icon: "fa-user",
              title: "Personal Information",
              items: ["Full name", "Email address", "Phone number", "Profile details (role, class, designation)", "Login credentials (securely stored)"]
            },
            {
              icon: "fa-graduation-cap",
              title: "Student & Academic Data",
              items: ["Attendance records", "Academic results and grades", "Assignments and submissions", "Class schedules and records"]
            },
            {
              icon: "fa-building-columns",
              title: "Institutional & Admin Data",
              items: ["Fee and payment records", "Communication logs", "Notifications and messages", "Reports and analytics"]
            },
            {
              icon: "fa-mobile-screen",
              title: "Device & Technical Data",
              items: ["Device type and operating system", "IP address", "App usage statistics", "Crash logs and performance data"]
            },
          ].map((item, i) => (
            <div className="col-md-6" key={i}>
              <div className="card shadow-sm h-100" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <div className="card-body p-4">
                  <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                  <h5 className="card-title mb-3">{item.title}</h5>
                  <ul className="list-unstyled mb-0">
                    {item.items.map((point, j) => (
                      <li key={j} className="mb-1 small">
                        <i className="fa-solid fa-circle-check me-2" style={{ color: primaryColor, fontSize: '0.75rem', transition: 'color 0.28s ease' }}></i>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How We Use */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>How We <span>Use Your Information</span></h3>
        </div>
        <div className="row g-4">
          {[
            { icon: "fa-gears", text: "Provide and maintain App functionality" },
            { icon: "fa-school", text: "Manage academic and administrative operations" },
            { icon: "fa-comments", text: "Enable communication between students, parents, and staff" },
            { icon: "fa-chart-bar", text: "Generate reports and insights for institutions" },
            { icon: "fa-rocket", text: "Improve performance, usability, and features" },
            { icon: "fa-shield-halved", text: "Ensure security and prevent unauthorized access" },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-4" key={i}>
              <div className="card shadow-sm h-100 text-center p-4" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                <p className="mb-0 fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

  {/* Data Sharing + Security side by side */}
<div className="container my-5" data-aos="fade-up">
  <div className="row align-items-stretch g-4">
    <div className="col-md-6 d-flex flex-column">
      <div className="section-title text-start">
        <h3>Data Sharing & <span>Disclosure</span></h3>
      </div>
      <p style={{ textAlign: "justify" }}>
        We respect your privacy and do <strong>not sell your personal data</strong>. We may share information only in the following cases:
      </p>
      <div className="d-flex flex-column gap-3 flex-grow-1">
        {[
          { icon: "fa-school", text: "Within the Institution – Accessible to authorized school personnel based on roles" },
          { icon: "fa-server", text: "Service Providers – Cloud hosting, analytics (Firebase), technical support" },
          { icon: "fa-gavel", text: "Legal Requirements – When required by law, regulation, or legal process" },
          { icon: "fa-handshake", text: "Business Partners – Only when necessary for service delivery and operations" },
        ].map((item, i) => (
          <div key={i} className="card shadow-sm flex-grow-1" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
            <div className="card-body d-flex align-items-center p-3">
              <i className={`fa-solid ${item.icon} fa-lg me-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
              <p className="mb-0 small">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="col-md-6 d-flex flex-column">
      <div className="section-title text-start">
        <h3>Data <span>Security</span></h3>
      </div>
      <p style={{ textAlign: "justify" }}>
        We implement strong security measures to protect your data. Despite our efforts, no system is completely secure.
      </p>
      <div className="d-flex flex-column gap-3 flex-grow-1">
        {[
          { icon: "fa-lock", text: "Encrypted data transmission (HTTPS/SSL)" },
          { icon: "fa-user-lock", text: "Role-based access control" },
          { icon: "fa-key", text: "Secure authentication mechanisms" },
          { icon: "fa-magnifying-glass", text: "Regular system monitoring and updates" },
        ].map((item, i) => (
          <div key={i} className="card shadow-sm flex-grow-1" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
            <div className="card-body d-flex align-items-center p-3">
              <i className={`fa-solid ${item.icon} fa-lg me-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
              <p className="mb-0 small">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</div>

      {/* User Rights */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Your <span>Rights</span></h3>
        </div>
        <div className="row g-4">
          {[
            { icon: "fa-eye", text: "Access your personal data" },
            { icon: "fa-pen-to-square", text: "Request corrections to inaccurate data" },
            { icon: "fa-trash-can", text: "Request deletion of your data" },
            { icon: "fa-hand", text: "Restrict or object to certain processing" },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-3" key={i}>
              <div className="card shadow-sm h-100 text-center p-4" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                <p className="mb-0 fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-muted">
          Requests should be directed through your respective institution or by contacting us at{" "}
          <a href="mailto:info@technicmentors.com" style={{ color: primaryColor, textDecoration: "none" }}>
            info@technicmentors.com
          </a>
        </p>
      </div>

      {/* App Permissions */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>App <span>Permissions</span></h3>
          <p className="lead" style={{ color: "#555" }}>We only request permissions necessary for proper App functionality:</p>
        </div>
        <div className="row g-4 justify-content-center">
          {[
            { icon: "fa-wifi", text: "Internet Access – Required for core functionality" },
            { icon: "fa-bell", text: "Notifications – For alerts and updates" },
            { icon: "fa-camera", text: "Camera – For profile images or document uploads" },
          ].map((item, i) => (
            <div className="col-md-4" key={i}>
              <div className="card shadow-sm h-100 text-center p-4" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                <p className="mb-0 fw-medium">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Policy Details */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div className="section-title text-center mb-4">
          <h3>Additional <span>Policy Details</span></h3>
        </div>
        <div className="row g-4">
          {[
            {
              icon: "fa-child",
              title: "Children's Privacy",
              desc: "CapoBrain may include users under 18. We do not collect data directly from children without authorization from their school. All student data is managed under institutional supervision."
            },
            {
              icon: "fa-arrow-up-right-from-square",
              title: "Third-Party Services",
              desc: "CapoBrain may use cloud hosting providers and analytics tools such as Firebase. These services may collect and process data according to their own privacy policies."
            },
            {
              icon: "fa-database",
              title: "Data Retention",
              desc: "We retain data only as long as necessary to provide services, comply with legal requirements, and maintain institutional records. Retention is primarily controlled by the respective institution."
            },
            {
              icon: "fa-certificate",
              title: "Data Safety & Compliance",
              desc: "Data is encrypted during transmission, access is restricted by user roles, and sensitive data is handled securely in line with applicable data protection laws."
            },
            {
              icon: "fa-globe",
              title: "International Transfers",
              desc: "As Technic Mentors operates globally, your data may be processed in Pakistan and other regions. Appropriate safeguards are in place for all such transfers."
            },
            {
              icon: "fa-rotate",
              title: "Policy Updates",
              desc: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Please review periodically."
            },
          ].map((item, i) => (
            <div className="col-md-6 col-lg-4" key={i}>
              <div className="card shadow-sm h-100" style={cardStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                <div className="card-body text-center p-4">
                  <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                  <h5 className="card-title mb-3">{item.title}</h5>
                  <p className="card-text small">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Us */}
      <div className="container my-5" data-aos="fade-up" data-aos-duration="1000">
        <div
          className="p-5 text-center"
          style={{
            background: "linear-gradient(135deg, rgba(104,77,244,0.08), rgba(104,77,244,0.03))",
            borderRadius: "16px",
            border: "1px solid rgba(104,77,244,0.2)",
          }}
        >
          <i className="fa-solid fa-envelope-open-text fa-3x mb-4" style={{ color: primaryColor }}></i>
          <div className="section-title text-center mb-3">
            <h3>Contact <span>Us</span></h3>
          </div>
          <p className="lead mb-4" style={{ color: "#555" }}>
            If you have any questions or concerns regarding this Privacy Policy, feel free to reach out.
          </p>
          <div className="row justify-content-center g-3">
            {[
             
              { icon: "fa-envelope", label: "Email", value: "info@technicmentors.com", href: "mailto:info@technicmentors.com" },
              { icon: "fa-phone", label: "Phone", value: "+92 311 1122144", href: "tel:+923111122144" },
            ].map((item, i) => (
              <div className="col-md-4" key={i}>
                <a href={item.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                  <div
                    className="card shadow-sm h-100 text-center p-4"
                    style={{ ...cardStyle, cursor: 'pointer' }}
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                  >
                    <i className={`fa-solid ${item.icon} fa-2x mb-3`} style={{ color: primaryColor, transition: 'color 0.28s ease' }}></i>
                    <p className="fw-bold mb-1">{item.label}</p>
                    <p style={{ fontSize: "0.9rem", marginBottom: 0 }}>{item.value}</p>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}